import { useState, useMemo, useEffect } from "react";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DataTable from "cxfcomponents/Tables/DataTable";
import Checkbox from "@mui/material/Checkbox";
import StatusBadge from "cxfcomponents/StatusBadge";

import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell";
import SoftBox from "components/SoftBox";

import { useAuth0 } from "@auth0/auth0-react";
import { deleteQuery } from "api/UdasWrapper";
// import { insertQuery } from "helpers/Graphql";
// import { deleteQuery } from "helpers/Graphql";
// import { updateQuery } from "helpers/Graphql";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SoftInput from "components/SoftInput";

function UsersModalUnshare({
  openUsersModalUnshare,
  setOpenUsersModalUnshare,
  users,
  roles,
  selectedUserFlow,
  userFlows,
  titleName,
  unshareList,
  setUnshareList,
  flowView,
}) {
  const { user } = useAuth0();
  const [toUnshareFlows, setToUnshareFlows] = useState([]);

  const [selectAllChecked, setSelectAllChecked] = useState(false);

  useEffect(() => {
    if (userFlows && selectedUserFlow) {
      const existingUserflows = userFlows.filter((item) => item.flow_id === selectedUserFlow.id);
      if (existingUserflows.length === 0) {
        setUnshareList([]);
      } else {
        const invitedUserIds = existingUserflows.map(
          (existingUserflow) => existingUserflow.user_id
        );

        const invitedUsers = users.filter(
          (item) =>
            item.auth0_id !== selectedUserFlow.created_by_id &&
            item.status === "active" &&
            invitedUserIds.includes(item.auth0_id)
        );
        setUnshareList(invitedUsers);
      }
    }
  }, [userFlows, selectedUserFlow]);

  const handleClose = () => {
    setOpenUsersModalUnshare(false);
    setSelectAllChecked(false);
    setToUnshareFlows([]);
  };

  const doesUserIdExist = (data, userIdToCheck) => {
    return !data.some((entry) => entry.user_id === userIdToCheck);
  };

  const handleSelectedUser = async (userData, isChecked) => {
    const { id, name } = userData;
    if (userFlows) {
      if (isChecked === true) {
        const userflowId = userFlows.find(
          (uf) => uf.flow_id === selectedUserFlow?.id && id === uf.user_id
        )?.id;

        if (doesUserIdExist(toUnshareFlows, id)) {
          setToUnshareFlows((prevToUnShareFlows) => [
            ...prevToUnShareFlows,
            {
              flow_id: selectedUserFlow?.id,
              flow_name: selectedUserFlow?.name,
              user_id: id,
              user_name: name,
              userflow_id: userflowId,
            },
          ]);
        }
      } else {
        // Handle unchecked state here
        if (selectAllChecked) {
          if (toUnshareFlows && toUnshareFlows.length <= 1) {
            setSelectAllChecked(false);
          }
        }
        setToUnshareFlows((prevToUnShareFlows) =>
          prevToUnShareFlows.filter(
            (item) => !(item.flow_id === selectedUserFlow?.id && item.user_id === id)
          )
        );
      }
    }
  };

  const isChecked = (userId) => {
    return toUnshareFlows?.some(
      ({ flow_id, user_id: flowUserId }) =>
        flow_id === selectedUserFlow?.id && flowUserId === userId
    );
  };

  const handleUnshareFlow = async () => {
    const filterUserFlowsId = toUnshareFlows
      .filter((flow) => flow.userflow_id)
      .map((userflowsId) => userflowsId.userflow_id);

    if (filterUserFlowsId.length === 0) {
      toast.warning(`Unable to unshare the flow, No users selected`);
      return;
    }

    let data = JSON.stringify({
      query: `mutation DeleteMultipleUserFlows($tenantId: String!, $userFlowIds: [ID]) {
        deleteMultipleUserFlows(tenant_id: $tenantId, user_flow_ids: $userFlowIds) {
          isSuccessful
          message
          resultObject
        }
      }`,
      variables: {
        tenantId: user?.stripeId,
        userFlowIds: filterUserFlowsId,
      },
    });
    try {
      const response = await deleteQuery(data);
      handleClose();
      if (response?.deleteMultipleUserFlows?.isSuccessful) {
        toast.success(`Flow ${selectedUserFlow?.name} has been unshared successfully!`);
      } else {
        toast.error(`Unable to unshare flow ${selectedUserFlow?.name}`);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const toggleSelectAll = () => {
    const selected = !selectAllChecked;
    setSelectAllChecked(selected);

    for (const user of users) {
      if (user?.auth0_id !== user?.cxfUserId) {
        handleSelectedUser(
          { id: user?.auth0_id, name: `${user?.first_name} ${user?.last_name}` },
          selected
        );
      }
    }
  };

  const dataTableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "unshare to",
          accessor: "selectUser",
          canGroupBy: false,
          disableSortBy: true,
          disableGlobalFilter: true,
          Cell: ({ value }) => {
            return (
              <Checkbox
                checked={isChecked(value.id)} // Step 4: Update checked state
                onChange={(event) => handleSelectedUser(value, event.target.checked)}
              />
            );
          },
        },
        {
          Header: "users",
          accessor: "users",
          width: 500,
          disableSortBy: false,
          canGroupBy: false,
          disableGlobalFilter: true,
          defaultSortOrder: "desc",
        },
        {
          Header: "name",
          accessor: "name",
          show: false,
        },
        {
          Header: "status",
          accessor: "status",
          Cell: ({ value }) => {
            return <StatusBadge status={value} />;
          },
          canGroupBy: false,
          disableGlobalFilter: true,
        },
        {
          Header: "access type",
          accessor: "accessType",
          canGroupBy: false,
          disableSortBy: true,
          disableGlobalFilter: true,
          Cell: ({ value }) => {
            const { id } = value;
            return (
              <SoftBox width="250px">
                <SoftInput
                  key={id}
                  value={
                    userFlows?.find(
                      (uf) => uf.flow_id === selectedUserFlow?.id && uf.user_id === id
                    )?.access_type === "readOnly"
                      ? "Read Only"
                      : "Editor"
                  }
                  disabled
                />
              </SoftBox>
            );
          },
        },
      ],
      rows:
        users && unshareList
          ? [
              ...unshareList.map((item) => {
                return {
                  selectUser: { id: item.auth0_id, name: `${item.first_name} ${item.last_name}` },
                  users: (
                    <ProductCellfromUser
                      id={item.auth0_id}
                      image={item.image}
                      name={`${item.first_name} ${item.last_name}`}
                      firstName={item.first_name}
                      lastName={item.last_name}
                      email={item.email_address}
                      checked={true}
                    />
                  ),
                  name: `${item.first_name} ${item.last_name}`,
                  role: (() => {
                    if (roles.length !== 0) {
                      if (!item.role_id) {
                        return "";
                      } else {
                        const roleName = roles.find((role) => role.id === item.role_id)?.name;
                        return roleName ? roleName : "";
                      }
                    } else {
                      return "";
                    }
                  })(),
                  lastActive: "",
                  status: item.status,
                  accessType: { id: item.auth0_id, name: `${item.first_name} ${item.last_name}` },
                };
              }),
            ]
          : [],
    };
  }, [users, roles, userFlows, selectedUserFlow, selectAllChecked, unshareList, toUnshareFlows]);

  return (
    <Dialog
      fullWidth
      open={openUsersModalUnshare}
      onClose={handleClose}
      PaperProps={{
        style: {
          maxWidth: "1020px",
        },
      }}
    >
      <DialogContent className="share-flow-content">
        <DialogTitle sx={{ marginLeft: "8px" }}>
          {titleName} Flow: {selectedUserFlow?.name}
        </DialogTitle>
        <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "100px" }} sx={{ marginLeft: "24px" }}>
          <SoftTypography variant="h6" mb={1}>
            <Checkbox
              checked={selectAllChecked}
              onChange={toggleSelectAll}
              indeterminate={
                selectAllChecked && userFlows?.some((flow) => flow.access_type === "editor")
              }
            />
            Select All
          </SoftTypography>
        </SoftBox>
        <DataTable
          table={dataTableData}
          hiddenTopButtons={true}
          flowView={flowView}
          withSearchbar={true}
        />
      </DialogContent>
      <DialogActions>
        <SoftButton variant="outlined" color="info" onClick={handleClose}>
          Close
        </SoftButton>
        <SoftButton
          color="info"
          onClick={handleUnshareFlow}
          disabled={toUnshareFlows?.length > 0 ? false : true}
        >
          {titleName}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

export default UsersModalUnshare;
